import React from 'react';
import { Link } from 'react-router-dom';

const MessagingConsentPage = () => {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">SMS Messaging Terms &amp; Consent</h1>
        <p className="mt-2 text-sm text-gray-500">Last updated: April 2026</p>
      </div>

      {/* Business identification */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-blue-600 text-sm text-gray-700">
        <h2 className="text-base font-semibold text-gray-900 mb-2">Business Information</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          <div><dt className="inline font-medium text-gray-900">Legal entity: </dt><dd className="inline">Winborne Software LLC</dd></div>
          <div><dt className="inline font-medium text-gray-900">Product: </dt><dd className="inline">Car Wash Copilot</dd></div>
          <div><dt className="inline font-medium text-gray-900">Website: </dt><dd className="inline">carwashcopilot.com</dd></div>
          <div><dt className="inline font-medium text-gray-900">Support email: </dt><dd className="inline"><a href="mailto:help@carwashcopilot.com" className="text-blue-600 hover:text-blue-800">help@carwashcopilot.com</a></dd></div>
          <div><dt className="inline font-medium text-gray-900">Support phone: </dt><dd className="inline">(484) 525-0026</dd></div>
          <div><dt className="inline font-medium text-gray-900">SMS sender: </dt><dd className="inline">+1 (888) 255-0484</dd></div>
        </dl>
      </div>

      <div className="bg-white rounded-lg shadow p-4 sm:p-8 space-y-8 text-sm text-gray-700 leading-relaxed">
        {/* Summary */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Summary</h2>
          <p>
            Car Wash Copilot is a subscription-based business management platform used by car wash
            operators to coordinate staff. We send SMS messages to two types of users only:
          </p>
          <ol className="list-decimal pl-5 space-y-1 mt-2">
            <li>
              <strong>Team members</strong> whose phone number has been entered into our platform by
              an authorized account owner or admin on their behalf (as part of employment or
              contractor onboarding).
            </li>
            <li>
              <strong>Prospective customers</strong> who submit our{' '}
              <Link to="/sms-opt-in" className="text-blue-600 hover:text-blue-800 font-medium">
                public SMS opt-in form
              </Link>{' '}
              and explicitly check the consent box.
            </li>
          </ol>
          <p className="mt-2">
            We never send marketing, promotional, or unsolicited messages. Phone numbers are never
            sold, rented, or shared with third parties for marketing purposes.
          </p>
        </section>

        {/* Consent flow #1: team invite */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            How Consent Is Collected &mdash; Team Invites
          </h2>
          <p>
            An account owner or admin logs into the Car Wash Copilot dashboard, opens{' '}
            <em>Settings &rarr; Team Members</em>, and clicks <em>+ Add Member</em>. They enter
            the team member's name and phone number on the following form:
          </p>

          {/* Mock "Add Member" form */}
          <div className="mt-4 border border-gray-300 rounded-lg bg-gray-50 p-4 sm:p-6">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Settings &rarr; Team Members &rarr; Add New Member
            </div>
            <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900">Add New Member</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                  <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-400 bg-white">
                    John Smith
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-400 bg-white">
                    (555) 123-4567
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
                <p className="font-semibold mb-1">Consent notice (shown to admin)</p>
                <p>
                  By clicking <strong>Add Member</strong>, you confirm that this team member has
                  agreed to receive SMS messages from Car Wash Copilot for account setup and
                  operational notifications. Message &amp; data rates may apply. Message frequency
                  varies. Reply STOP to opt out, HELP for help.
                </p>
              </div>
              <button
                type="button"
                disabled
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium opacity-90 cursor-default"
              >
                Add Member
              </button>
            </div>
          </div>

          <p className="mt-4">
            The account owner attests that the team member has agreed to receive messages. Car
            Wash Copilot then sends a one-time invite SMS to the phone number provided. The
            complete text of that invite is shown below:
          </p>

          {/* Invite SMS bubble */}
          <div className="mt-4 max-w-md">
            <div className="text-xs text-gray-500 mb-1">
              From <strong>+1 (888) 255-0484</strong>
            </div>
            <div className="bg-gray-100 border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-gray-800 whitespace-pre-line">
{`Jane Doe invited you to join Ocean City Car Wash on Car Wash Copilot. Open the app to get started: https://carwashcopilot.com

Reply STOP to opt out of messages. Msg & data rates may apply.`}
            </div>
          </div>
        </section>

        {/* Consent flow #2: public opt-in form */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            How Consent Is Collected &mdash; Public Opt-In Form
          </h2>
          <p>
            Prospective customers and anyone else who wishes to receive messages from us can opt
            in through our public form at{' '}
            <Link to="/sms-opt-in" className="text-blue-600 hover:text-blue-800 font-medium">
              carwashcopilot.com/sms-opt-in
            </Link>
            . That form requires the user to enter their name and phone number and to check an
            explicit, initially-unchecked consent box before submission is accepted. The exact
            consent language on the form is:
          </p>
          <blockquote className="mt-3 border-l-4 border-gray-300 pl-4 italic text-gray-600">
            "I agree to receive SMS messages from Car Wash Copilot (Winborne Software LLC)
            regarding my account and the information I have requested. Message frequency varies
            (typically fewer than 5 messages/month). Message &amp; data rates may apply. Reply STOP
            to opt out or HELP for help. See our Messaging Terms and Privacy practices."
          </blockquote>
          <p className="mt-3">
            After submission, we send a confirmation SMS acknowledging the opt-in and reiterating
            STOP/HELP instructions. The complete text of that confirmation is shown below:
          </p>

          {/* Opt-in confirmation SMS bubble */}
          <div className="mt-4 max-w-md">
            <div className="text-xs text-gray-500 mb-1">
              From <strong>+1 (888) 255-0484</strong>
            </div>
            <div className="bg-gray-100 border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-gray-800">
              Hi Jane, you're now subscribed to SMS from Car Wash Copilot. Msgs: account &amp; operational notifications. Msg freq varies. Msg &amp; data rates may apply. Reply HELP for help, STOP to cancel.
            </div>
          </div>
        </section>

        {/* Message types */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Types of Messages We Send</h2>
          <ul className="list-disc pl-5 space-y-2 mt-1">
            <li>
              <strong>Team invites</strong> &mdash; a one-time SMS sent when an account owner adds
              a new team member, containing a link to access the app.
            </li>
            <li>
              <strong>Work order assignments</strong> &mdash; transactional notifications when a
              work order is assigned to a team member. Example:
              <div className="mt-2 max-w-md">
                <div className="bg-gray-100 border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-gray-800 whitespace-pre-line">
{`Jane Doe assigned you a work order: "Replace tire shine pump". Open the app to view details: https://carwashcopilot.com

Reply STOP to opt out of messages. Msg & data rates may apply.`}
                </div>
              </div>
            </li>
            <li>
              <strong>Account &amp; billing alerts</strong> &mdash; account approval status,
              trial/subscription reminders, and similar operational messages.
            </li>
            <li>
              <strong>Opt-in confirmations</strong> &mdash; one-time SMS sent after a user submits
              the public opt-in form, confirming their subscription.
            </li>
          </ul>
          <p className="mt-3">
            We do not send marketing, promotional, advertising, or political messages.
          </p>
        </section>

        {/* Frequency, rates, STOP/HELP */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Frequency, Rates &amp; Help</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Message frequency:</strong> varies based on account activity. Typical users
              receive fewer than 5 messages per month.
            </li>
            <li>
              <strong>Message &amp; data rates:</strong> standard message and data rates may apply
              per your wireless plan. Car Wash Copilot does not charge for messages.
            </li>
            <li>
              <strong>Opt out:</strong> reply <strong>STOP</strong> to any message to opt out of
              all future SMS from Car Wash Copilot. You will receive one final confirmation.
            </li>
            <li>
              <strong>Help:</strong> reply <strong>HELP</strong> to any message, or email{' '}
              <a href="mailto:help@carwashcopilot.com" className="text-blue-600 hover:text-blue-800">
                help@carwashcopilot.com
              </a>
              , or call (484) 525-0026.
            </li>
            <li>
              <strong>Carriers:</strong> supported on all major U.S. carriers. Carriers are not
              liable for delayed or undelivered messages.
            </li>
          </ul>
        </section>

        {/* Data use */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Data Use &amp; Privacy</h2>
          <p>
            Phone numbers collected through team invites or the public opt-in form are used solely
            to deliver the transactional SMS messages described above and to authenticate users
            into the Car Wash Copilot platform. Phone numbers are never sold, rented, or shared
            with third parties for marketing purposes. Messages are transmitted through Twilio,
            our licensed SMS provider. For details on how we protect your data, see our{' '}
            <Link to="/security" className="text-blue-600 hover:text-blue-800">
              Security &amp; Data Protection
            </Link>{' '}
            page.
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Contact</h2>
          <p>
            Winborne Software LLC &middot;{' '}
            <a href="mailto:help@carwashcopilot.com" className="text-blue-600 hover:text-blue-800">
              help@carwashcopilot.com
            </a>{' '}
            &middot; (484) 525-0026
          </p>
        </section>
      </div>

      <div className="text-sm text-gray-600">
        Want to opt in to receive messages?{' '}
        <Link to="/sms-opt-in" className="text-blue-600 hover:text-blue-800 font-medium">
          Visit our opt-in form &rarr;
        </Link>
      </div>
    </div>
  );
};

export default MessagingConsentPage;
